import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { fetchUserHostedTournaments, fetchUserJoinedTournaments } from '../services/tournament';
import { Tournament } from '../services/tournament.types';
import { useAuth } from './authStore';

type TournamentState = {
  hostedTournaments: Tournament[];
  joinedTournaments: Tournament[];
  loading: boolean;
  setHostedTournaments: (tournaments: Tournament[]) => void;
  setJoinedTournaments: (tournaments: Tournament[]) => void;
  fetchTournaments: () => Promise<void>;
  clearTournaments: () => void;
};

const TournamentContext = createContext<TournamentState | undefined>(undefined);

export const TournamentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hostedTournaments, setHostedTournamentsState] = useState<Tournament[]>([]);
  const [joinedTournaments, setJoinedTournamentsState] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(false);
  const { authenticatedUser, getUserCountry } = useAuth();

  const fetchTournaments = useCallback(async () => {
    if (!authenticatedUser?.id) return;
    
    try {
      setLoading(true);
      const userCountry = getUserCountry();
      
      const [hostedResult, joinedResult] = await Promise.all([
        fetchUserHostedTournaments(1, 20, userCountry),
        fetchUserJoinedTournaments(1, 20, userCountry)
      ]);
      
      setHostedTournamentsState(hostedResult.tournaments);
      setJoinedTournamentsState(joinedResult.tournaments);
    } catch {
      setHostedTournamentsState([]);
      setJoinedTournamentsState([]);
    } finally {
      setLoading(false);
    }
  }, [authenticatedUser?.id, getUserCountry]);

  const setHostedTournaments = (tournaments: Tournament[]) => {
    setHostedTournamentsState(tournaments);
  };

  const setJoinedTournaments = (tournaments: Tournament[]) => {
    setJoinedTournamentsState(tournaments);
  };

  const clearTournaments = () => {
    setHostedTournamentsState([]);
    setJoinedTournamentsState([]);
  };

  useEffect(() => {
    if (authenticatedUser) {
      fetchTournaments();
    } else {
      clearTournaments();
    }
  }, [authenticatedUser, fetchTournaments]);

  return (
    <TournamentContext.Provider value={{
      hostedTournaments,
      joinedTournaments,
      loading,
      setHostedTournaments,
      setJoinedTournaments,
      fetchTournaments,
      clearTournaments
    }}>
      {children}
    </TournamentContext.Provider>
  );
};

export const useTournaments = () => {
  const ctx = useContext(TournamentContext);
  if (!ctx) {
    throw new Error('useTournaments must be used within TournamentProvider');
  }
  return ctx;
};
